import moment from 'moment';
import React from 'react';
import toMoney from '~/common/masked/toMoney';
import { Quote } from '~/ui/assets/icons';
import { Flex, Typography, Divider, Tag } from '~/ui/components';
import { AllocationPayment, DataprevPayment, ExternalPayment, Installment } from '~/typings/entities/Payments';
import {
  PaymentOriginNames,
  PaymentStatusColors,
  PaymentStatusNames,
  PaymentTypeNames,
  ManagedStatusNames,
  PaymentModeNames,
  ExternalPaymentStatusNames,
  ExternalPaymentStatusColors,
} from '~/typings/enums/Payment';

export type InstallmentDetailsProps = {
  installment: Installment;
};

type SectionInfoProps = {
  title: string;
  infoRows?: { label: string; value: string | React.ReactNode; props?: any }[];
};

function SectionInfo({ title, infoRows }: SectionInfoProps) {
  if (!infoRows || infoRows.length === 0) return null;

  return (
    <Flex direction="column" gap={8}>
      <Typography type="headingXSmall" weight={700}>
        {title}
      </Typography>

      <Flex direction="column" gap={4}>
        {infoRows?.map((row, index) => (
          <Flex key={index} align="center" justify="space-between" height={24}>
            <Typography type="bodySmall" color="element.secondary">
              {row.label}
            </Typography>
            <Typography type="bodySmall" weight={600} {...row?.props}>
              {row.value}
            </Typography>
          </Flex>
        ))}
      </Flex>

      <Divider margin={16} lineType="solid" />
    </Flex>
  );
}

export function InstallmentDetails({ installment }: InstallmentDetailsProps) {
  const renderMode = (mode: string) => {
    if (!mode) return '-';
    return PaymentModeNames?.[mode] || mode;
  };

  const renderType = (type: string) => {
    if (!type) return '-';
    return PaymentTypeNames?.[type] || type;
  };

  const renderOrigin = (origin: string) => {
    if (!origin) return '-';
    return PaymentOriginNames?.[origin] || origin;
  };

  const renderStatus = (status: string) => {
    if (!status) return '-';
    const colors = PaymentStatusColors?.[status] || PaymentStatusColors.DEFAULT;
    return <Tag {...colors}>{PaymentStatusNames?.[status] || status}</Tag>;
  };

  const renderExternalStatus = (status: string) => {
    if (!status) return '-';
    const colors = ExternalPaymentStatusColors?.[status] || ExternalPaymentStatusColors.DEFAULT;
    return <Tag {...colors}>{ExternalPaymentStatusNames?.[status] || status}</Tag>;
  };

  const renderManagedStatus = (managedStatus: string) =>
    PaymentStatusNames?.[managedStatus] || ManagedStatusNames?.[managedStatus] || managedStatus;

  const getAllocationPayments = (allocationPayments: AllocationPayment[]) => {
    if (!allocationPayments?.length) return [];

    const payments = allocationPayments.map((payment) => [
      { label: 'Data recebimento', value: moment(payment.receivedDate).format('DD/MM/YYYY') },
      { label: 'Valor', value: toMoney(payment.amount), props: { weight: 700 } },
      { label: 'Origem', value: renderOrigin(payment.origin) },
      { label: 'Tipo', value: renderType(payment.type) },
    ]);

    return payments.reduce((acc, current, index) => {
      if (index === 0) return [...current];
      return [...acc, { label: '', value: '' }, ...current];
    }, []);
  };

  const getExternalPayments = (extrenalPayments: ExternalPayment[]) => {
    if (!extrenalPayments?.length) return [];

    const payments = extrenalPayments.map((payment) => [
      { label: 'Data pagamento', value: moment(payment.paymentDate).format('DD/MM/YYYY') },
      { label: 'Valor', value: toMoney(payment.value), props: { weight: 700 } },
      { label: 'Status', value: renderExternalStatus(payment.paymentStatus) },
      { label: 'Tipo', value: renderType(payment.paymentType) },
      { label: 'Modo', value: renderMode(payment.paymentMode) },
      { label: 'Origem', value: renderOrigin(payment.origin) },
    ]);

    return payments.reduce((acc, current, index) => {
      if (index === 0) return [...current];
      return [...acc, { label: '', value: '' }, ...current];
    }, []);
  };

  const getDataprevPayments = (dataprevPayments: DataprevPayment[]) => {
    if (!dataprevPayments?.length) return [];

    const payments = dataprevPayments.map((payment) => [
      { label: 'Competência', value: payment.installmentCompetence },
      { label: 'Valor pago', value: toMoney(payment.paidInstallmentValue), props: { weight: 700 } },
      { label: 'Status', value: renderExternalStatus(payment.status) },
      { label: 'CCB', value: payment.ccb },
    ]);

    return payments.reduce((acc, current, index) => {
      if (index === 0) return [...current];
      return [...acc, { label: '', value: '' }, ...current];
    }, []);
  };

  return (
    <Flex direction="column" mt={16}>
      <Quote height={40} width={40} color="#D80073" />

      {installment?.remainingAmountToPay && (
        <>
          <Flex align="center" justify="space-between" mt={12}>
            <Typography type="bodySmall" weight={700}>
              Valor do boleto
            </Typography>

            <Typography type="bodyMedium" weight={700}>
              {toMoney(installment?.remainingAmountToPay)}
            </Typography>
          </Flex>

          <Typography type="bodyXSmall" color="element.secondary">
            Valor residual de responsabilidade do cliente
          </Typography>
        </>
      )}

      <Divider margin={16} lineType="solid" />

      <SectionInfo
        title="Informações básicas"
        infoRows={[
          { label: 'Vencimento', value: moment(installment.dueDate).format('DD/MM/YYYY') },
          { label: 'Valor', value: toMoney(installment.amount), props: { weight: 700 } },
          { label: 'Carência', value: installment.gracePeriod ? `${installment.gracePeriod} dias` : 'Nenhuma' },
          { label: 'IOF (dias)', value: installment.iofDays ? `${installment.iofDays} dias` : 'Nenhuma' },
          { label: 'IOF (valor)', value: toMoney(installment.iof) },
        ]}
      />

      <SectionInfo
        title="Status"
        infoRows={[
          { label: 'Status', value: renderStatus(installment.status) },
          { label: 'Responsável parcela', value: renderManagedStatus(installment.managedStatus) },
          { label: 'Status externo', value: renderStatus(installment.externalStatus) },
        ]}
      />

      <SectionInfo
        title="Composição do valor"
        infoRows={[
          { label: 'Juros', value: toMoney(installment.interest) },
          { label: 'Principal', value: toMoney(installment.principalValue) },
          { label: 'Saldo inicial', value: toMoney(installment.initialBalance) },
          { label: 'Saldo final', value: toMoney(installment.finalBalance) },
        ]}
      />

      <SectionInfo title="Pagamentos alocados" infoRows={getAllocationPayments(installment.allocationPayments)} />

      <SectionInfo title="Pagamentos externos" infoRows={getExternalPayments(installment.externalPayments)} />

      <SectionInfo title="Pagamentos Dataprev" infoRows={getDataprevPayments(installment.dataprevPayments)} />
    </Flex>
  );
}
